import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CsrfService } from '../../auth/csrf.service';
import { ConfigService } from '../../config/config.service';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function normalizeGatewayOrigin(gatewayUrl: string): string {
  return gatewayUrl.replace(/\/+$/, '');
}

function normalizeGatewayBase(gatewayUrl: string): string {
  return `${normalizeGatewayOrigin(gatewayUrl)}/`;
}

function toGatewayApiUrl(url: string, gatewayUrl: string): string {
  if (!url.startsWith('/api/')) {
    return url;
  }

  return `${normalizeGatewayBase(gatewayUrl)}${url.replace(/^\/+/, '')}`;
}

function isGatewayApiRequest(url: string, gatewayUrl: string): boolean {
  if (url.startsWith('/api/')) {
    return true;
  }

  try {
    const requestUrl = new URL(url, window.location.origin);
    const gatewayOrigin = normalizeGatewayOrigin(gatewayUrl);

    return (
      requestUrl.origin === gatewayOrigin &&
      requestUrl.pathname.startsWith('/api/')
    );
  } catch {
    return false;
  }
}

function readRequestTargetDomainForAshHost(
  requestUrl: string,
  gatewayUrl: string,
): string {
  try {
    return new URL(requestUrl, window.location.origin).hostname;
  } catch {
    return new URL(normalizeGatewayOrigin(gatewayUrl)).hostname;
  }
}

export const xsrfInterceptor: HttpInterceptorFn = (req, next) => {
  const configService = inject(ConfigService);
  const csrfService = inject(CsrfService);
  const gatewayUrl = configService.getConfig('gatewayUrl')!;
  const rewrittenUrl = toGatewayApiUrl(req.url, gatewayUrl);
  const request =
    rewrittenUrl === req.url ? req : req.clone({ url: rewrittenUrl });

  if (!isGatewayApiRequest(request.url, gatewayUrl)) {
    return next(request);
  }

  const requestWithHeader = request.clone({
    // x-ash-host identifies the domain the API request is sent to.
    setHeaders: {
      'x-ash-host': readRequestTargetDomainForAshHost(request.url, gatewayUrl),
    },
  });
  const requestWithCredentials = request.withCredentials
    ? requestWithHeader
    : requestWithHeader.clone({ withCredentials: true });

  if (SAFE_METHODS.has(request.method.toUpperCase())) {
    return next(requestWithCredentials);
  }

  const xsrfToken = csrfService.getCsrfToken();
  if (!xsrfToken) {
    return next(requestWithCredentials);
  }

  return next(
    requestWithCredentials.clone({
      setHeaders: {
        'X-CSRF-TOKEN': xsrfToken,
      },
    }),
  );
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const configService = inject(ConfigService);
  const gatewayUrl = configService.getConfig('gatewayUrl')!;
  const rewrittenUrl = toGatewayApiUrl(req.url, gatewayUrl);
  const request =
    rewrittenUrl === req.url ? req : req.clone({ url: rewrittenUrl });

  if (!isGatewayApiRequest(request.url, gatewayUrl)) {
    return next(request);
  }

  const requestWithAshHost = request.clone({
    setHeaders: {
      'x-ash-host': readRequestTargetDomainForAshHost(request.url, gatewayUrl),
    },
  });

  if (requestWithAshHost.withCredentials) {
    return next(requestWithAshHost);
  }

  return next(requestWithAshHost.clone({ withCredentials: true }));
};
